/** leads/AtvFilterCard.tsx — 올댓벤처스 리스트 상단 필터 카드(3단)와 «같은 구성» (2026-09-10)
 *   ① 찾기(검색·지역·투자연도·조건 버튼) / 보기(정렬·표시 개수 — 결과 줄 오른쪽)
 *   ② 금액·재무 조건 패널 — 환급예상액·투자금액·연매출·영업이익 범위(만원 단위, 프리셋) + 증자 건수 [1][2][3][4][5+] · 「조회」로 적용
 *   ③ 적용 조건·결과 — N건 / 전체 M건 · 조건 칩(✕) · 전체 해제 · 선택 n건
 *  ATV atv-web/admin/js/views/list.js buildFilterCard 의 React 판. 값 규칙(만원→원, 한쪽만 이상/이하, 적자 토글 배타)도 같다. */
import React, { useEffect, useRef, useState } from 'react';

export interface RangeDef { key: string; label: string; unit?: string; signed?: boolean; presets: { label: string; min?: string; max?: string; loss?: boolean }[] }
export interface Chip { k: string; v: string; rm: () => void }
export interface RangeValues { [k: string]: number | boolean | undefined }   // refundMin/refundMax(원) … opLoss(bool)

interface Props {
  searchPlaceholder: string;
  q: string;
  onQ: (q: string) => void;                       // 350ms 디바운스·Enter 적용
  find?: React.ReactNode;                         // 검색창 뒤 셀렉트들
  findAfter?: React.ReactNode;                    // 조건 버튼 뒤 (토글 등)
  view?: React.ReactNode;                         // 보기(정렬·개수)
  ranges?: RangeDef[];                            // 금액·재무 조건 (없으면 패널 없음)
  rangeValues?: RangeValues;                      // 적용 상태 (원 단위)
  onRanges?: (v: RangeValues) => void;            // 「조회」/초기화 → 적용
  rounds?: { value: number[]; onChange: (v: number[]) => void; label?: string };
  panelExtra?: React.ReactNode;                   // 패널 안 추가 행 (label/cell 쌍은 호출측에서 f-rk/f-subcell로)
  panelLabel?: string;                            // 조건 버튼 라벨 (기본 '금액 · 재무 조건')
  hint?: string;
  total: number;
  grandTotal?: number | null;
  chips: Chip[];
  onResetAll: () => void;
  selectedCount?: number;
  loading?: boolean;
}

const toWon = (v: string, signed?: boolean): number | undefined => {
  const t = String(v || '').trim();
  if (!t) return undefined;
  const n = Number(t.replace(signed ? /[^0-9.-]/g : /[^0-9.]/g, ""));
  if (!Number.isFinite(n)) return undefined;
  if (!signed && n <= 0) return undefined;
  return Math.round(n * 1e4);
};
const toUnit = (won: number | undefined): string => (won == null ? '' : String((won / 1e4) % 1 === 0 ? won / 1e4 : Number((won / 1e4).toFixed(2))));
export const moneyLabel = (won: number | undefined): string => {
  if (won == null || !Number.isFinite(Number(won))) return '';
  const n = Number(won); if (n === 0) return '0원';
  const sign = n < 0 ? '-' : ''; const a = Math.abs(n);
  if (a >= 1e8) { const v = a / 1e8; return sign + (v % 1 === 0 ? v : Number(v.toFixed(2))) + '억'; }
  const v = a / 1e4; return sign + (v % 1 === 0 ? v : Number(v.toFixed(1))) + '만';
};
export const rangeLabel = (min?: number, max?: number): string => {
  const lo = min != null ? moneyLabel(min) : ''; const hi = max != null ? moneyLabel(max) : '';
  if (lo && hi) return lo + '~' + hi; if (lo) return lo + ' 이상'; if (hi) return hi + ' 이하'; return '';
};

export default function AtvFilterCard(p: Props) {
  const [open, setOpen] = useState(false);
  const [qv, setQv] = useState(p.q);
  const tRef = useRef<number | null>(null);
  useEffect(() => { setQv(p.q); }, [p.q]);
  const applyQ = (v: string) => { if (tRef.current) window.clearTimeout(tRef.current); p.onQ(v.trim()); };
  const onInput = (v: string) => {
    setQv(v);
    if (tRef.current) window.clearTimeout(tRef.current);
    tRef.current = window.setTimeout(() => p.onQ(v.trim()), 350);
  };

  // 패널 임시값(staged) — 열 때 적용 상태를 되끌어 온다
  const [staged, setStaged] = useState<Record<string, string>>({});
  const [stagedLoss, setStagedLoss] = useState(false);
  const [stagedRounds, setStagedRounds] = useState<number[]>([]);
  const pull = () => {
    const s: Record<string, string> = {};
    (p.ranges || []).forEach((r) => {
      s[r.key + 'Min'] = toUnit(p.rangeValues?.[r.key + 'Min'] as number | undefined);
      s[r.key + 'Max'] = toUnit(p.rangeValues?.[r.key + 'Max'] as number | undefined);
    });
    setStaged(s); setStagedLoss(!!p.rangeValues?.opLoss); setStagedRounds([...(p.rounds?.value || [])]);
  };
  const toggle = () => { const o = !open; setOpen(o); if (o) pull(); };
  const commit = () => {
    const v: RangeValues = {};
    (p.ranges || []).forEach((r) => {
      v[r.key + 'Min'] = toWon(staged[r.key + 'Min'] || '', r.signed);
      v[r.key + 'Max'] = toWon(staged[r.key + 'Max'] || '', r.signed);
    });
    if (stagedLoss) { v.opLoss = true; v.opMin = undefined; v.opMax = undefined; } else v.opLoss = false;
    p.onRanges && p.onRanges(v);
    p.rounds && p.rounds.onChange([...stagedRounds]);
  };
  const resetPanel = () => {
    const s: Record<string, string> = {}; (p.ranges || []).forEach((r) => { s[r.key + 'Min'] = ''; s[r.key + 'Max'] = ''; });
    setStaged(s); setStagedLoss(false); setStagedRounds([]);
    const v: RangeValues = {}; (p.ranges || []).forEach((r) => { v[r.key + 'Min'] = undefined; v[r.key + 'Max'] = undefined; }); v.opLoss = false;
    p.onRanges && p.onRanges(v); p.rounds && p.rounds.onChange([]);
  };
  const advCount = (p.ranges || []).reduce((a, r) => a + ((p.rangeValues?.[r.key + 'Min'] != null || p.rangeValues?.[r.key + 'Max'] != null) ? 1 : 0), 0)
    + (p.rangeValues?.opLoss ? 1 : 0) + (p.rounds && p.rounds.value.length ? 1 : 0);
  const hasPanel = !!((p.ranges && p.ranges.length) || p.rounds || p.panelExtra);

  const presetOn = (r: RangeDef, d: { min?: string; max?: string; loss?: boolean }) => d.loss ? stagedLoss : ((staged[r.key + 'Min'] || '') === (d.min || '') && (staged[r.key + 'Max'] || '') === (d.max || ''));
  const setPreset = (r: RangeDef, d: { min?: string; max?: string; loss?: boolean }) => {
    if (d.loss) { const on = !stagedLoss; setStagedLoss(on); if (on) setStaged((s) => ({ ...s, [r.key + 'Min']: '', [r.key + 'Max']: '' })); return; }
    const on = presetOn(r, d);
    setStaged((s) => ({ ...s, [r.key + 'Min']: on ? '' : (d.min || ''), [r.key + 'Max']: on ? '' : (d.max || '') }));
    if (r.key === 'op') setStagedLoss(false);
  };

  return (
    <div className="atv-fcard">
      <div className="atv-ffind">
        <span className="atv-glabel">찾기</span>
        <div className="atv-fsearch">
          <span className="atv-fsico">⌕</span>
          <input className="atv-fsin" placeholder={p.searchPlaceholder} value={qv} onChange={(e) => onInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') applyQ(qv); }} />
          {qv && <button type="button" className="atv-fclear" title="검색어 지우기" onClick={() => { setQv(''); applyQ(''); }}>✕</button>}
        </div>
        {p.find}
        {hasPanel && (
          <button type="button" className={'atv-advbtn' + (advCount > 0 ? ' active' : '') + (open ? ' open' : '')} onClick={toggle}>
            {p.panelLabel || '금액 · 재무 조건'}
            {advCount > 0 && <span className="atv-fbadge">{advCount}</span>}
            <span className="atv-fcaret">{open ? '▲' : '▼'}</span>
          </button>
        )}
        {p.findAfter}
      </div>
      {hasPanel && open && (
        <div className="atv-fadv">
          <div className="atv-fgrid">
            {(p.ranges || []).map((r) => (
              <React.Fragment key={r.key}>
                <span className="atv-frk">{r.label}</span>
                <span className="atv-frange">
                  <input className="atv-fin" placeholder="최소" inputMode={r.signed ? 'text' : 'decimal'} value={staged[r.key + 'Min'] || ''}
                    onChange={(e) => { setStaged((s) => ({ ...s, [r.key + 'Min']: e.target.value })); if (r.key === 'op' && e.target.value.trim()) setStagedLoss(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
                  <span className="atv-ftilde">~</span>
                  <input className="atv-fin" placeholder="최대" inputMode={r.signed ? 'text' : 'decimal'} value={staged[r.key + 'Max'] || ''}
                    onChange={(e) => { setStaged((s) => ({ ...s, [r.key + 'Max']: e.target.value })); if (r.key === 'op' && e.target.value.trim()) setStagedLoss(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
                  <span className="atv-funit">{r.unit || '만원'}</span>
                </span>
                <span className="atv-fpresets">
                  {r.presets.map((d) => <button key={d.label} type="button" className={'atv-fpreset' + (presetOn(r, d) ? ' on' : '')} onClick={() => setPreset(r, d)}>{d.label}</button>)}
                </span>
              </React.Fragment>
            ))}
            {p.rounds && (<>
              <span className="atv-frk">{p.rounds.label || '증자 건수'}</span>
              <span className="atv-frounds">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" className={'atv-fround' + (stagedRounds.includes(n) ? ' on' : '')}
                    onClick={() => setStagedRounds((a) => (a.includes(n) ? a.filter((x) => x !== n) : [...a, n].sort((x, y) => x - y)))}>{n === 5 ? '5+' : n}</button>
                ))}
                <span className="atv-fnote">여러 개 선택 가능</span>
              </span>
            </>)}
            {p.panelExtra}
          </div>
          <div className="atv-fadvfoot">
            <span className="atv-funithint">{p.hint || '금액은 모두 만원 단위입니다 (예: 3000 = 3천만원 · 10000 = 1억) · 한쪽만 넣으면 이상 / 이하로 검색됩니다 · 영업이익은 -5000 처럼 음수도 됩니다'}</span>
            <button type="button" className="atv-freset" onClick={resetPanel}>조건 초기화</button>
            <button type="button" className="atv-fapply" onClick={commit}>조회</button>
          </div>
        </div>
      )}
      <div className="atv-fapplied">
        <span className="atv-fcount"><b>{p.total.toLocaleString()}</b>건{p.grandTotal != null && <span className="atv-fall"> / 전체 {p.grandTotal.toLocaleString()}건</span>}{p.loading && <span className="atv-fall"> · 조회 중…</span>}</span>
        {p.chips.length > 0 ? (<>
          <span className="atv-fvrule" />
          {p.chips.map((c, i) => (
            <span key={i} className="atv-fchip">{c.k && <span className="atv-fchipk">{c.k}</span>}{c.v}
              <button type="button" className="atv-fchipx" title="이 조건 해제" onClick={c.rm}>✕</button></span>
          ))}
          <button type="button" className="atv-fclearall" onClick={p.onResetAll}>전체 해제</button>
        </>) : <span className="atv-fnone">적용된 조건 없음 — 전체 목록입니다</span>}
        {/* 보기(정렬·개수·회사/건별)는 결과 줄 오른쪽에 — 찾기 줄 아래 따로 떠 있던 배치가 어색하다는 피드백(2026-09-10). 선택 건수는 0건이면 숨김 */}
        {(p.view || (p.selectedCount != null && p.selectedCount > 0)) && (
          <div className="atv-fview">
            {p.selectedCount != null && p.selectedCount > 0 && <span className="atv-fselcount"><b>{p.selectedCount}</b>건 선택됨</span>}
            {p.view && <><span className="atv-glabel">보기</span>{p.view}</>}
          </div>
        )}
      </div>
    </div>
  );
}
